import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Phone } from 'lucide-react';
import { Button } from './ui/button';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/assessment', label: 'Home Assessment' },
    { 
      label: 'Services',
      dropdown: [
        { path: '/services', label: 'Maintenance & Care' },
        { path: '/remodeling', label: 'Remodeling & Construction' }
      ]
    },
    { path: '/how-it-works', label: 'Our Process' },
    { 
      label: 'About',
      dropdown: [
        { path: '/about', label: 'Our Story' },
        { path: '/why-valenrock', label: 'Why Valenrock' },
        { path: '/reviews', label: 'Reviews' }
      ]
    }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      {/* Top Bar - Contact Info */}
      <div className="bg-[#003366] text-white py-2 hidden md:block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center text-sm">
          <div className="flex items-center gap-6">
            <span className="text-white/70">Northern & Central NJ</span>
            <span className="text-white/40">|</span>
            <span className="text-white/70">License #13VH13590600</span>
          </div>
          <div className="flex items-center gap-6">
            <a href="tel:5513799774" className="flex items-center gap-2 hover:text-[#CFB973] transition-colors">
              <Phone size={14} />
              <span>(551) 379-9774</span>
            </a>
            <a href="mailto:contact@valenrockhome.services" className="hover:text-[#CFB973] transition-colors">
              contact@valenrockhome.services
            </a>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="bg-white border-b-4 border-[#CFB973] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center group">
              <img 
                src="https://customer-assets.emergentagent.com/job_waitlist-system-5/artifacts/dxfo1yhd_Logo-03.png" 
                alt="Valenrock Home Services" 
                className="h-14 sm:h-16 w-auto transition-all duration-300 group-hover:scale-105"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8">
              {navLinks.map((link, index) => (
                link.dropdown ? (
                  <div 
                    key={index}
                    className="relative group"
                    onMouseEnter={() => setActiveDropdown(link.label)}
                    onMouseLeave={() => setActiveDropdown(null)}
                  >
                    <button
                      className={`text-base font-bold transition-colors duration-200 flex items-center space-x-1 py-2 ${
                        link.dropdown.some(sub => isActive(sub.path))
                          ? 'text-[#003366]'
                          : 'text-gray-700 hover:text-[#003366]'
                      }`}
                    >
                      <span>{link.label}</span>
                      <ChevronDown size={16} className={`transition-transform ${activeDropdown === link.label ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {activeDropdown === link.label && (
                      <div className="absolute top-full left-0 pt-2 w-64">
                        <div className="bg-white border-2 border-[#CFB973] rounded-xl shadow-xl py-2 overflow-hidden">
                          {link.dropdown.map((subLink) => (
                            <Link
                              key={subLink.path}
                              to={subLink.path}
                              className={`block px-4 py-3 text-sm font-medium transition-colors ${
                                isActive(subLink.path)
                                  ? 'bg-[#003366] text-white'
                                  : 'text-gray-700 hover:bg-[#E8EEF3] hover:text-[#003366]'
                              }`}
                            >
                              {subLink.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`text-base font-bold transition-colors duration-200 relative ${
                      isActive(link.path)
                        ? 'text-[#003366]'
                        : 'text-gray-700 hover:text-[#003366]'
                    }`}
                  >
                    {link.label}
                    {isActive(link.path) && (
                      <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-[#CFB973]"></span>
                    )}
                  </Link>
                )
              ))}
            </div>

            {/* CTA Button */}
            <div className="hidden lg:flex items-center gap-4">
              <Link to="/contact" className="bg-[#003366] hover:bg-[#003366]/90 text-white px-6 py-3 font-bold shadow-lg rounded-md">
                Get Started
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-[#003366] hover:bg-[#E8EEF3] rounded-lg transition-colors"
            >
              {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t-2 border-[#E8EEF3]">
            <div className="px-4 py-6 space-y-2">
              {navLinks.map((link, index) => (
                link.dropdown ? (
                  <div key={index} className="space-y-1">
                    <div className="px-4 py-2 text-sm font-bold text-[#003366]">
                      {link.label}
                    </div>
                    {link.dropdown.map((subLink) => (
                      <Link
                        key={subLink.path}
                        to={subLink.path}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`block px-8 py-3 text-sm font-medium rounded-xl transition-colors ${
                          isActive(subLink.path)
                            ? 'text-white bg-[#003366]'
                            : 'text-gray-700 hover:text-[#003366] hover:bg-[#E8EEF3]'
                        }`}
                      >
                        {subLink.label}
                      </Link>
                    ))}
                  </div>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`block px-4 py-3 text-sm font-semibold rounded-xl transition-colors ${
                      isActive(link.path)
                        ? 'text-white bg-[#003366]'
                        : 'text-gray-700 hover:text-[#003366] hover:bg-[#E8EEF3]'
                    }`}
                  >
                    {link.label}
                  </Link>
                )
              ))}
              
              {/* Mobile Contact Info */}
              <div className="pt-4 mt-4 border-t-2 border-[#E8EEF3] space-y-3">
                <a 
                  href="tel:5513799774" 
                  className="flex items-center gap-3 px-4 py-3 bg-[#E8EEF3] rounded-xl"
                >
                  <div className="w-10 h-10 rounded-full bg-[#CFB973] flex items-center justify-center">
                    <Phone size={18} className="text-[#003366]" />
                  </div>
                  <span className="font-bold text-[#003366]">(551) 379-9774</span>
                </a>
                <Link to="/contact" onClick={() => setMobileMenuOpen(false)} className="block w-full bg-[#003366] hover:bg-[#003366]/90 text-white py-4 font-bold text-lg text-center rounded-md">
                  Get Started
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
